import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { ProfileSwitcherService } from '@core/services/profile-switcher.service';

import { AvatarSize, type AvatarConfig } from '@shared/components/avatar/avatar.types';
import type { DynamicTextConfig } from '@shared/components/dynamic-text/dynamic-text.types';
import { Dynamic, type DynamicAction } from '@shared/registry';
import { getInitials } from '@shared/utils/string-utils';

import { type ProfileSelectorConfig, type ProfileSelectorSize } from './profile-selector.types';

const PROFILE_TO_AVATAR_SIZE: Record<ProfileSelectorSize, AvatarSize> = {
  sm: AvatarSize.SM,
  lg: AvatarSize.LG,
};

const ICON_SIZE: Record<ProfileSelectorSize, string> = {
  sm: '16',
  lg: '24',
};

@Component({
  selector: 'app-profile-selector',
  templateUrl: './profile-selector.html',
  imports: [NgStyle, Dynamic],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileSelector {
  private readonly profileSwitcherService = inject(ProfileSwitcherService);

  readonly config = input.required<ProfileSelectorConfig>();

  readonly action = output<DynamicAction | undefined>();

  // Derive state from ProfileSwitcherService.isOpen
  protected readonly state = computed(() => (this.profileSwitcherService.isOpen() ? 'active' : 'initial'));

  protected readonly iconSize = computed(() => ICON_SIZE[this.config().size]);

  protected readonly gapVariable = computed(() => `var(--profile-selector-${this.config().size}-gap)`);

  protected readonly buttonStyle = computed(() => {
    const size = this.config().size;
    const state = this.state();
    const base = {
      gap: this.gapVariable(),
      padding: `var(--profile-selector-${size}-padding-y) var(--profile-selector-${size}-padding-x-end) var(--profile-selector-${size}-padding-y) var(--profile-selector-${size}-padding-x-start)`,
      borderRadius: `var(--profile-selector-${size}-border-radius)`,
      fontFamily: 'var(--profile-selector-font-family)',
    };

    if (size === 'lg') {
      const lgInnerBackground =
        state === 'active'
          ? 'var(--profile-selector-lg-active-background)'
          : 'var(--profile-selector-lg-initial-background)';
      return {
        ...base,
        // Gradient border technique: borderColor must be transparent so the gradient
        // defined in border-box shows through, while padding-box provides the solid inner fill.
        borderColor: 'transparent',
        background: `linear-gradient(${lgInnerBackground}, ${lgInnerBackground}) padding-box, linear-gradient(90deg, var(--gradients-primary-stop), var(--gradients-primary-middle), var(--gradients-primary-start)) border-box`,
      };
    }

    return { ...base, background: `var(--profile-selector-${size}-${state}-background)` };
  });

  protected readonly textConfig = computed<DynamicTextConfig>(() => ({
    text: this.config().name,
    size: this.config().size === 'sm' ? 'sm' : 'xl',
    fontWeight: this.config().size === 'sm' ? 'bold' : 'semibold',
    fontFamily: 'sans',
    cssVarPrefix: `profile-selector-${this.config().size}-${this.state()}`,
  }));

  protected readonly iconColor = computed(
    () => `var(--profile-selector-${this.config().size}-${this.state()}-icon-color)`,
  );

  protected readonly avatarConfig = computed<AvatarConfig>(() => ({
    size: PROFILE_TO_AVATAR_SIZE[this.config().size],
    imageUrl: this.config().avatarUrl,
    profileId: this.config().profileId,
    initials: this.config().avatarUrl ? undefined : this.config().initials || getInitials(this.config().name),
  }));

  protected onClick(): void {
    this.action.emit(this.config().action);
  }
}
